"use client";

import { Link, useLocation } from "react-router-dom";
import { LanguageLink } from "@/components/LanguageLink";
import { DashboardHeader } from "./components";
import { WatermarkTemplateEditor } from "./components/WatermarkTemplateEditor";
import { useSubscription, useQuotaStatus, usePortal } from "@/lib/hooks";
import { useInstagramAccounts, useInstagramActions } from "@/lib/hooks/useInstagram";
import { useTikTokAccounts, useTikTokActions } from "@/lib/hooks/useTikTok";
import { useYouTubeAccounts, useYouTubeActions } from "@/lib/hooks/useYouTube";
import { useCurrency } from "@/lib/hooks/useCurrency";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Loader2, 
  CreditCard, 
  ArrowRight, 
  CheckCircle2, 
  AlertCircle, 
  Clock, 
  Instagram, 
  Trash2, 
  ExternalLink, 
  Youtube, 
  Layout, 
  Share2,
  Music,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { usePricingPlans } from "../landing/components/PricingSection";
import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@/components/ui/use-toast";

type SettingsTab = "subscription" | "integrations" | "templates";

export default function AccountPage() {
  const { hash, search } = useLocation();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { currency } = useCurrency();
  const plans = usePricingPlans(currency);
  
  const [activeTab, setActiveTab] = useState<SettingsTab>("subscription");

  // Sync active tab with hash
  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    if (id === "instagram" || id === "tiktok" || id === "youtube" || id === "integrations") {
      setActiveTab("integrations");
    } else if (id === "templates") {
      setActiveTab("templates");
    } else {
      setActiveTab("subscription");
    }
  }, [hash]);

  // Handle Stripe redirect params
  useEffect(() => {
    const params = new URLSearchParams(search);
    if (params.has("checkout")) {
      queryClient.invalidateQueries({ queryKey: ["subscription"] });
      queryClient.invalidateQueries({ queryKey: ["quota"] });

      if (params.get("upgraded") === "true") {
        toast({ title: t("account.upgradeSuccess", "Plan upgraded!"), description: t("account.upgradeSuccessDesc", "Your new plan is now active.") });
      } else if (params.get("downgrade_scheduled") === "true") {
        toast({ title: t("account.downgradeScheduled", "Downgrade scheduled"), description: t("account.downgradeScheduledDesc", "Your plan will change at the end of the current billing period.") });
      }

      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, "", cleanUrl);
    }
  }, [search, queryClient, t]);

  const { data: subscription, isLoading: isSubLoading } = useSubscription();
  const { data: quota, isLoading: isQuotaLoading } = useQuotaStatus();
  const { data: instagramData, isLoading: isIgLoading } = useInstagramAccounts();
  const instagramActions = useInstagramActions();
  const { data: tiktokData, isLoading: isTkLoading } = useTikTokAccounts();
  const tiktokActions = useTikTokActions();
  const { data: youtubeData, isLoading: isYtLoading } = useYouTubeAccounts();
  const youtubeActions = useYouTubeActions();
  const portalMutation = usePortal();

  const [connectingIg, setConnectingIg] = useState(false);
  const [connectingTk, setConnectingTk] = useState(false);
  const [connectingYt, setConnectingYt] = useState(false);
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);
  const [pendingDisconnect, setPendingDisconnect] = useState<{ id: string; platform: "instagram" | "tiktok" | "youtube" } | null>(null);
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});

  const handlePortal = async () => {
    try {
      await portalMutation.mutateAsync();
    } catch (error) {
      console.error("Failed to open portal:", error);
    }
  };

  const handleConnectInstagram = async () => {
    setConnectingIg(true);
    try {
      const { authUrl } = await instagramActions.getAuthUrl();
      window.location.href = authUrl;
    } catch (error) {
      toast({
        title: t("instagram.connectError"),
        description: error instanceof Error ? error.message : t("errors.defaultMessage"),
        variant: "destructive",
      });
      setConnectingIg(false);
    }
  };

  const handleDisconnectInstagram = async (accountId: string) => {
    setDisconnectingId(accountId);
    setPendingDisconnect(null);
    try {
      await instagramActions.disconnect(accountId);
      await queryClient.invalidateQueries({ queryKey: ["instagram-accounts"] });
      toast({
        title: t("instagram.disconnected"),
        description: t("instagram.disconnectedDesc"),
      });
    } catch (error) {
      toast({
        title: t("instagram.disconnectError"),
        description: error instanceof Error ? error.message : t("errors.defaultMessage"),
        variant: "destructive",
      });
    } finally {
      setDisconnectingId(null);
    }
  };

  const handleConnectYouTube = async () => {
    setConnectingYt(true);
    try {
      const { authUrl } = await youtubeActions.getAuthUrl();
      window.location.href = authUrl;
    } catch (error) {
      toast({
        title: t("youtube.connectError"),
        description: error instanceof Error ? error.message : t("errors.defaultMessage"),
        variant: "destructive",
      });
      setConnectingYt(false);
    }
  };

  const handleDisconnectYouTube = async (accountId: string) => {
    setDisconnectingId(accountId);
    setPendingDisconnect(null);
    try {
      await youtubeActions.disconnect(accountId);
      await queryClient.invalidateQueries({ queryKey: ["youtube-accounts"] });
      toast({
        title: t("youtube.disconnected"),
        description: t("youtube.disconnectedDesc"),
      });
    } catch (error) {
      toast({
        title: t("youtube.disconnectError"),
        description: error instanceof Error ? error.message : t("errors.defaultMessage"),
        variant: "destructive",
      });
    } finally {
      setDisconnectingId(null);
    }
  };

  const handleConnectTikTok = async () => {
    setConnectingTk(true);
    try {
      const { authUrl } = await tiktokActions.getAuthUrl();
      window.location.href = authUrl;
    } catch (error) {
      toast({
        title: t("tiktok.connectError"),
        description: error instanceof Error ? error.message : t("errors.defaultMessage"),
        variant: "destructive",
      });
      setConnectingTk(false);
    }
  };

  const handleDisconnectTikTok = async (accountId: string) => {
    setDisconnectingId(accountId);
    setPendingDisconnect(null);
    try {
      await tiktokActions.disconnect(accountId);
      await queryClient.invalidateQueries({ queryKey: ["tiktok-accounts"] });
      toast({
        title: t("tiktok.disconnected"),
        description: t("tiktok.disconnectedDesc"),
      });
    } catch (error) {
      toast({
        title: t("tiktok.disconnectError"),
        description: error instanceof Error ? error.message : t("errors.defaultMessage"),
        variant: "destructive",
      });
    } finally {
      setDisconnectingId(null);
    }
  };

  const isLoading = isSubLoading || isQuotaLoading;

  const handleConfirmDisconnect = () => {
    if (!pendingDisconnect) return;
    if (pendingDisconnect.platform === "instagram") {
      handleDisconnectInstagram(pendingDisconnect.id);
    } else if (pendingDisconnect.platform === "tiktok") {
      handleDisconnectTikTok(pendingDisconnect.id);
    } else {
      handleDisconnectYouTube(pendingDisconnect.id);
    }
  };

  const navItems = [
    { id: "subscription", label: t("account.nav.subscription"), icon: CreditCard },
    { id: "integrations", label: t("account.nav.integrations"), icon: Share2 },
    { id: "templates", label: t("account.nav.templates"), icon: Layout },
  ] as const;

  return (
    <div className="min-h-screen bg-lavender">
      <main className="max-w-7xl 2k:max-w-[80%] 4k:max-w-[80%] mx-auto px-6 py-8">
        <DashboardHeader />

        <div className="mt-8 2k:mt-24 4k:mt-36">
          <h1 className="font-h2 mb-4 2k:mb-12 4k:mb-20 2k:text-5xl 4k:text-8xl">{t("account.title")}</h1>
          <p className="font-body mb-12 2k:mb-24 4k:mb-32 max-w-2xl 2k:max-w-4xl 4k:max-w-6xl 2k:text-2xl 4k:text-4xl text-muted-foreground/80">
            {t("account.description")}
          </p>

          <div className="flex flex-col gap-8 2k:gap-16 4k:gap-24">
            {/* Horizontal Tabs Navigation */}
            <div className="w-fit shrink-0">
              <nav className="flex flex-row gap-2 2k:gap-4 4k:gap-6 bg-white/50 p-1.5 rounded-2xl border border-black/5 overflow-x-auto no-scrollbar">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={cn(
                      "flex items-center gap-3 2k:gap-6 4k:gap-10 px-6 py-2.5 2k:px-12 2k:py-5 4k:px-18 4k:py-8 rounded-xl transition-all font-link text-sm 2k:text-2xl 4k:text-4xl whitespace-nowrap cursor-pointer",
                      activeTab === item.id 
                        ? "bg-black text-white shadow-lg shadow-black/10" 
                        : "hover:bg-white text-muted-foreground hover:text-foreground"
                    )}
                  >
                    <item.icon className={cn("size-4 2k:size-8 4k:size-12", activeTab === item.id ? "text-white" : "text-muted-foreground")} />
                    <p className={cn("hidden md:flex", activeTab === item.id ? "flex" : "")}>{item.label}</p>
                  </button>
                ))}
              </nav>
            </div>

            {/* Content Area */}
            <div className="flex-1 min-w-0">
              {activeTab === "subscription" && (
                <div className="space-y-8 2k:space-y-16 4k:space-y-24 animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 2k:gap-16 4k:gap-24">
                    {/* Subscription Card */}
                    <div className="bg-white rounded-2xl p-8 2k:p-14 4k:p-20 shadow-sm border border-black/5 flex flex-col">
                      <div className="flex items-center justify-between mb-8 2k:mb-14 4k:mb-20">
                        <div className="flex items-center gap-3 2k:gap-8 4k:gap-14">
                          <div className="p-3 2k:p-6 4k:p-10 rounded-xl bg-black text-white">
                            <CreditCard className="w-6 h-6 2k:w-12 2k:h-12 4k:w-20 4k:h-20" />
                          </div>
                          <div>
                            <p className="text-sm 2k:text-2xl 4k:text-4xl text-muted-foreground font-medium mb-1">{t("account.subscription")}</p>
                            <h2 className="text-2xl 2k:text-5xl 4k:text-7xl font-bold">
                              {isLoading ? t("common.loading") : subscription?.hasSubscription ? subscription.planName : t("account.noActivePlan")}
                            </h2>
                          </div>
                        </div>

                        {!isLoading && subscription?.hasSubscription && (
                          <div className={cn(
                            "px-4 py-1.5 rounded-full text-sm font-bold 2k:text-2xl 2k:px-8 2k:py-4 4k:text-4xl 4k:px-12 4k:py-6 border",
                            subscription.status === 'active' || subscription.status === 'trialing'
                              ? "bg-green-50 text-green-700 border-green-200"
                              : "bg-amber-50 text-amber-700 border-amber-200"
                          )}>
                            {subscription.status === 'active' ? t("account.statusActive") : subscription.status === 'canceled' ? t("account.statusCanceled") : subscription.status}
                          </div>
                        )}
                      </div>

                      <div className="space-y-6 2k:space-y-12 4k:space-y-18 grow">
                        {isLoading ? (
                          <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                          </div>
                        ) : !subscription?.hasSubscription ? (
                          <div className="bg-lavender/50 rounded-xl p-6 2k:p-12 4k:p-18 border border-dashed border-black/10">
                            <p className="font-body text-center mb-6">{t("account.noSubscription")}</p>
                            <Button 
                              asChild 
                              className="w-full bg-black hover:bg-black/90 text-white rounded-xl 2k:rounded-2xl h-12 2k:h-24 4k:h-36 px-4 2k:px-8! 4k:px-12! text-md 2k:text-3xl 4k:text-5xl font-bold"
                            >
                              <LanguageLink to="/?section=pricing">
                                {t("account.seePricing")}
                                <ArrowRight className="ml-2 w-4 h-4 2k:w-8 2k:h-8 4k:w-16 4k:h-16" />
                              </LanguageLink>
                            </Button>
                          </div>
                        ) : (
                          <>
                            <div className="grid grid-cols-2 gap-4 2k:gap-8 4k:gap-12">
                              <div className="bg-lavender/30 p-4 2k:p-8 4k:p-12 rounded-xl">
                                <p className="text-xs 2k:text-xl 4k:text-3xl text-muted-foreground mb-1 2k:mb-3 4k:mb-5">{t("account.price")}</p>
                                <p className="text-lg 2k:text-4xl 4k:text-6xl font-bold">
                                  {(() => {
                                    const plan = plans.find(
                                      e => e.priceTier === subscription.planName.toLowerCase()
                                    );
                                    if (!plan) return "-";
                                    return currency === "eur"
                                      ? `€${plan.price}/month`
                                      : `${plan.price} kr/month`;
                                  })()}
                                </p>
                              </div>
                              <div className="bg-lavender/30 p-4 2k:p-8 4k:p-12 rounded-xl">
                                <p className="text-xs 2k:text-xl 4k:text-3xl text-muted-foreground mb-1 2k:mb-3 4k:mb-5">{t("account.nextInvoice")}</p>
                                <p className="text-lg 2k:text-4xl 4k:text-6xl font-bold">
                                  {subscription.currentPeriodEnd ? new Date(subscription.currentPeriodEnd).toLocaleDateString('sv-SE') : '-'}
                                </p>
                              </div>
                            </div>

                            {subscription.cancelAtPeriodEnd && (
                              <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-800">
                                <Clock className="w-5 h-5 shrink-0" />
                                <p className="text-sm 2k:text-2xl 4k:text-4xl">
                                  {t("account.expiresOn", { date: new Date(subscription.currentPeriodEnd!).toLocaleDateString('sv-SE') })}
                                </p>
                              </div>
                            )}

                            <div className="flex flex-col sm:flex-row gap-4 pt-4 mt-auto">
                              <Button 
                                onClick={handlePortal}
                                disabled={portalMutation.isPending} 
                                variant="outline"
                                className="flex-1 rounded-lg 2k:rounded-2xl border-2 border-black/10 hover:bg-black/5 h-12 2k:h-24 4k:h-36 px-4 2k:px-8! 4k:px-12! text-md 2k:text-3xl 4k:text-5xl font-bold cursor-pointer"
                              >
                                {portalMutation.isPending ? <Loader2 className="w-4 h-4 2k:w-8 2k:h-8 animate-spin mr-2" /> : <CreditCard className="size-4 mr-2 2k:size-10 4k:size-16" />}
                                {t("account.managePayment")}
                              </Button>
                              <Button 
                                asChild 
                                className="flex-1 bg-black hover:bg-black/90 text-white rounded-lg 2k:rounded-2xl h-12 2k:h-24 4k:h-36 px-4 2k:px-8! 4k:px-12! text-md 2k:text-3xl 4k:text-5xl font-bold"
                              >
                                <LanguageLink to="/?section=pricing">
                                  {t("account.changePlan")}
                                  <ArrowRight className="ml-2 size-4 2k:size-8 4k:size-16" />
                                </LanguageLink>
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Usage Card */}
                    <div className="bg-white rounded-2xl p-8 2k:p-14 4k:p-20 shadow-sm border border-black/5 flex flex-col">
                      <div className="flex items-center gap-3 2k:gap-8 4k:gap-14 mb-12 2k:mb-24 4k:mb-32">
                        <div className="p-3 2k:p-6 4k:p-10 rounded-xl bg-lavender text-black">
                          <CheckCircle2 className="w-6 h-6 2k:w-12 2k:h-12 4k:w-20 4k:h-20" />
                        </div>
                        <div>
                          <p className="text-sm 2k:text-2xl 4k:text-4xl text-muted-foreground font-medium mb-1">{t("account.usage")}</p>
                          <h2 className="text-2xl 2k:text-5xl 4k:text-7xl font-bold">{t("account.quotaStatus")}</h2>
                        </div>
                      </div>

                      {isLoading ? (
                        <div className="flex items-center justify-center py-20">
                          <Loader2 className="w-12 h-12 animate-spin text-lavender-dark" />
                        </div>
                      ) : !quota?.hasActiveSubscription ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                          <AlertCircle className="w-12 h-12 text-muted-foreground mb-4 2k:w-20 2k:h-20 4k:w-32 4k:h-32" />
                          <p className="font-body text-muted-foreground max-w-xs 2k:max-w-xl 4k:max-w-2xl 2k:text-2xl 4k:text-4xl">
                            {t("account.activateSubscription")}
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-12 2k:space-y-24 4k:space-y-32 h-full flex flex-col">
                          <div>
                            <div className="flex justify-between items-end mb-4 2k:mb-8 4k:mb-12">
                              <div>
                                <p className="text-sm 2k:text-2xl 4k:text-4xl text-muted-foreground uppercase tracking-widest font-bold">{t("account.videosCreated")}</p>
                                <p className="text-4xl 2k:text-7xl 4k:text-9xl font-bold mt-1">
                                  {quota.videosUsed} / {quota.videosLimit}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm 2k:text-2xl 4k:text-4xl text-muted-foreground font-bold">{t("account.remaining", { count: quota.videosRemaining })}</p>
                              </div>
                            </div>
                            
                            {/* Progress Bar */}
                            <div className="h-6 2k:h-12 4k:h-20 bg-lavender rounded-full overflow-hidden">
                              <div 
                                className={cn(
                                  "h-full transition-all duration-1000 ease-out rounded-full",
                                  (quota.videosUsed / quota.videosLimit) > 0.9 ? "bg-amber-500" : "bg-black"
                                )}
                                style={{ width: `${Math.min(100, (quota.videosUsed / quota.videosLimit) * 100)}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "integrations" && (
                <div className="space-y-8 2k:space-y-16 4k:space-y-24 animate-in fade-in slide-in-from-bottom-4 duration-500">
                  {/* Instagram Connected Accounts */}
                  <div id="instagram">
                    <div className="bg-white rounded-2xl p-8 2k:p-14 4k:p-20 shadow-sm border border-black/5">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-8 2k:mb-14 4k:mb-20">
                        <div className="flex items-center gap-3 2k:gap-8 4k:gap-14">
                          <div className="p-3 2k:p-6 4k:p-10 rounded-xl bg-linear-to-br from-purple-500 to-pink-500 text-white">
                            <Instagram className="w-6 h-6 2k:w-12 2k:h-12 4k:w-20 4k:h-20" />
                          </div>
                          <div>
                            <p className="text-sm 2k:text-2xl 4k:text-4xl text-muted-foreground font-medium mb-1">{t("instagram.integrations")}</p>
                            <h2 className="text-2xl 2k:text-5xl 4k:text-7xl font-bold">{t("instagram.connectedAccounts")}</h2>
                          </div>
                        </div>

                        <Button
                          onClick={handleConnectInstagram}
                          disabled={connectingIg}
                          className="bg-linear-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-xl 2k:rounded-2xl h-10 2k:h-20 4k:h-32 text-sm 2k:text-2xl 4k:text-4xl px-4 2k:px-8! 4k:px-12!"
                        >
                          {connectingIg ? (
                            <Loader2 className="w-4 h-4 2k:w-8 2k:h-8 animate-spin mr-2" />
                          ) : (
                            <Instagram className="size-4 mr-2 2k:size-10 4k:size-10" />
                          )}
                          {t("instagram.connectAccount")}
                        </Button>
                      </div>

                      {isIgLoading ? (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                        </div>
                      ) : !instagramData?.accounts?.length ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                          <Instagram className="w-12 h-12 text-muted-foreground/30 mb-4 2k:w-20 2k:h-20 4k:w-32 4k:h-32" />
                          <p className="font-body text-muted-foreground max-w-md 2k:max-w-xl 4k:max-w-2xl 2k:text-2xl 4k:text-4xl">
                            {t("instagram.noAccounts")}
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-4 2k:space-y-8 4k:space-y-12">
                          {instagramData.accounts.map((account) => (
                            <div
                              key={account.id}
                              className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 2k:p-8 4k:p-12 bg-lavender/30 rounded-xl border border-black/5"
                            >
                              <div className="flex items-center gap-4 2k:gap-8 4k:gap-12">
                                {account.profilePictureUrl && !imageErrors[account.id] ? (
                                  <img
                                    src={account.profilePictureUrl}
                                    alt={account.instagramUsername}
                                    onError={() => setImageErrors((prev) => ({ ...prev, [account.id]: true }))}
                                    className="w-10 h-10 2k:w-20 2k:h-20 4k:w-32 4k:h-32 rounded-full object-cover"
                                  />
                                ) : (
                                  <div className="w-10 h-10 2k:w-20 2k:h-20 4k:w-32 4k:h-32 rounded-full bg-linear-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white">
                                    <Instagram className="size-5 2k:size-10 4k:size-16" />
                                  </div>
                                )}
                                <div>
                                  <p className="font-bold text-sm 2k:text-2xl 4k:text-4xl">@{account.instagramUsername}</p>
                                  <p className="text-xs 2k:text-xl 4k:text-3xl text-muted-foreground">
                                    {t("instagram.connectedOn", { date: new Date(account.connectedAt).toLocaleDateString("sv-SE") })}
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center gap-2 2k:gap-4 4k:gap-6">
                                <a
                                  href={`https://instagram.com/${account.instagramUsername}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-muted-foreground hover:text-foreground transition-colors"
                                >
                                  <ExternalLink className="size-4 2k:size-8 4k:size-12" />
                                </a>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setPendingDisconnect({ id: account.id, platform: "instagram" })}
                                  disabled={disconnectingId === account.id}
                                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                >
                                  {disconnectingId === account.id ? (
                                    <Loader2 className="size-4 2k:size-8 4k:size-12 animate-spin" />
                                  ) : (
                                    <Trash2 className="size-4 2k:size-8 4k:size-12" />
                                  )}
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* TikTok Connected Accounts */}
                  <div id="tiktok" className="mt-8 2k:mt-16 4k:mt-24">
                    <div className="bg-white rounded-2xl p-8 2k:p-14 4k:p-20 shadow-sm border border-black/5">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-8 2k:mb-14 4k:mb-20">
                        <div className="flex items-center gap-3 2k:gap-8 4k:gap-14">
                          <div className="p-3 2k:p-6 4k:p-10 rounded-xl bg-black text-white">
                            <Music className="w-6 h-6 2k:w-12 2k:h-12 4k:w-20 4k:h-20" />
                          </div>
                          <div>
                            <p className="text-sm 2k:text-2xl 4k:text-4xl text-muted-foreground font-medium mb-1">{t("tiktok.integrations")}</p>
                            <h2 className="text-2xl 2k:text-5xl 4k:text-7xl font-bold">{t("tiktok.connectedAccounts")}</h2>
                          </div>
                        </div>

                        <Button
                          onClick={handleConnectTikTok}
                          disabled={connectingTk}
                          className="bg-black hover:bg-black/90 text-white rounded-xl 2k:rounded-2xl h-10 2k:h-20 4k:h-32 text-sm 2k:text-2xl 4k:text-4xl px-4 2k:px-8! 4k:px-12!"
                        >
                          {connectingTk ? (
                            <Loader2 className="w-4 h-4 2k:w-8 2k:h-8 animate-spin mr-2" />
                          ) : (
                            <Music className="size-4 mr-2 2k:size-10 4k:size-10" />
                          )}
                          {t("tiktok.connectAccount")}
                        </Button>
                      </div>

                      {isTkLoading ? (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                        </div>
                      ) : !tiktokData?.accounts?.length ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                          <Music className="w-12 h-12 text-muted-foreground/30 mb-4 2k:w-20 2k:h-20 4k:w-32 4k:h-32" />
                          <p className="font-body text-muted-foreground max-w-md 2k:max-w-xl 4k:max-w-2xl 2k:text-2xl 4k:text-4xl">
                            {t("tiktok.noAccounts")}
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-4 2k:space-y-8 4k:space-y-12">
                          {tiktokData.accounts.map((account) => (
                            <div
                              key={account.id}
                              className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 2k:p-8 4k:p-12 bg-lavender/30 rounded-xl border border-black/5"
                            >
                              <div className="flex items-center gap-4 2k:gap-8 4k:gap-12">
                                {account.avatarUrl && !imageErrors[account.id] ? (
                                  <img
                                    src={account.avatarUrl}
                                    alt={account.tiktokUsername}
                                    onError={() => setImageErrors((prev) => ({ ...prev, [account.id]: true }))}
                                    className="w-10 h-10 2k:w-20 2k:h-20 4k:w-32 4k:h-32 rounded-full object-cover"
                                  />
                                ) : (
                                  <div className="w-10 h-10 2k:w-20 2k:h-20 4k:w-32 4k:h-32 rounded-full bg-black flex items-center justify-center text-white">
                                    <Music className="size-5 2k:size-10 4k:size-16" />
                                  </div>
                                )}
                                <div>
                                  <p className="font-bold text-sm 2k:text-2xl 4k:text-4xl">@{account.tiktokUsername}</p>
                                  <p className="text-xs 2k:text-xl 4k:text-3xl text-muted-foreground">
                                    {t("tiktok.connectedOn", { date: new Date(account.connectedAt).toLocaleDateString("sv-SE") })}
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center gap-2 2k:gap-4 4k:gap-6">
                                <a
                                  href={`https://tiktok.com/@${account.tiktokUsername}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-muted-foreground hover:text-foreground transition-colors"
                                >
                                  <ExternalLink className="size-4 2k:size-8 4k:size-12" />
                                </a>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setPendingDisconnect({ id: account.id, platform: "tiktok" })}
                                  disabled={disconnectingId === account.id}
                                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                >
                                  {disconnectingId === account.id ? (
                                    <Loader2 className="size-4 2k:size-8 4k:size-12 animate-spin" />
                                  ) : (
                                    <Trash2 className="size-4 2k:size-8 4k:size-12" />
                                  )}
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* YouTube Connected Accounts */}
                  <div id="youtube">
                    <div className="bg-white rounded-2xl p-8 2k:p-14 4k:p-20 shadow-sm border border-black/5">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-8 2k:mb-14 4k:mb-20">
                        <div className="flex items-center gap-3 2k:gap-8 4k:gap-14">
                          <div className="p-3 2k:p-6 4k:p-10 rounded-xl bg-red-600 text-white">
                            <Youtube className="w-6 h-6 2k:w-12 2k:h-12 4k:w-20 4k:h-20" />
                          </div>
                          <div>
                            <p className="text-sm 2k:text-2xl 4k:text-4xl text-muted-foreground font-medium mb-1">{t("youtube.integrations")}</p>
                            <h2 className="text-2xl 2k:text-5xl 4k:text-7xl font-bold">{t("youtube.connectedAccounts")}</h2>
                          </div>
                        </div>

                        <Button
                          onClick={handleConnectYouTube}
                          disabled={connectingYt}
                          className="bg-red-600 hover:bg-red-700 text-white rounded-xl 2k:rounded-2xl h-10 2k:h-20 4k:h-32 text-sm 2k:text-2xl 4k:text-4xl px-4 2k:px-8! 4k:px-12!"
                        >
                          {connectingYt ? (
                            <Loader2 className="w-4 h-4 2k:w-8 2k:h-8 animate-spin mr-2" />
                          ) : (
                            <Youtube className="size-4 mr-2 2k:size-10 4k:size-10" />
                          )}
                          {t("youtube.connectAccount")}
                        </Button>
                      </div>

                      {isYtLoading ? (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                        </div>
                      ) : !youtubeData?.accounts?.length ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                          <Youtube className="w-12 h-12 text-muted-foreground/30 mb-4 2k:w-20 2k:h-20 4k:w-32 4k:h-32" />
                          <p className="font-body text-muted-foreground max-w-md 2k:max-w-xl 4k:max-w-2xl 2k:text-2xl 4k:text-4xl">
                            {t("youtube.noAccounts")}
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-4 2k:space-y-8 4k:space-y-12">
                          {youtubeData.accounts.map((account) => (
                            <div
                              key={account.id}
                              className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 2k:p-8 4k:p-12 bg-lavender/30 rounded-xl border border-black/5"
                            >
                              <div className="flex items-center gap-4 2k:gap-8 4k:gap-12">
                                {account.avatarUrl && !imageErrors[account.id] ? (
                                  <img
                                    src={account.avatarUrl}
                                    alt={account.channelTitle}
                                    onError={() => setImageErrors((prev) => ({ ...prev, [account.id]: true }))}
                                    className="w-10 h-10 2k:w-20 2k:h-20 4k:w-32 4k:h-32 rounded-full object-cover"
                                  />
                                ) : (
                                  <div className="w-10 h-10 2k:w-20 2k:h-20 4k:w-32 4k:h-32 rounded-full bg-red-600 flex items-center justify-center text-white">
                                    <Youtube className="size-5 2k:size-10 4k:size-16" />
                                  </div>
                                )}
                                <div>
                                  <p className="font-bold text-sm 2k:text-2xl 4k:text-4xl">{account.channelTitle}</p>
                                  <p className="text-xs 2k:text-xl 4k:text-3xl text-muted-foreground">
                                    {t("youtube.connectedOn", { date: new Date(account.connectedAt).toLocaleDateString("sv-SE") })}
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center gap-2 2k:gap-4 4k:gap-6">
                                <a
                                  href={`https://www.youtube.com/channel/${account.youtubeChannelId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-muted-foreground hover:text-foreground transition-colors"
                                >
                                  <ExternalLink className="size-4 2k:size-8 4k:size-12" />
                                </a>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setPendingDisconnect({ id: account.id, platform: "youtube" })}
                                  disabled={disconnectingId === account.id}
                                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                >
                                  {disconnectingId === account.id ? (
                                    <Loader2 className="size-4 2k:size-8 4k:size-12 animate-spin" />
                                  ) : (
                                    <Trash2 className="size-4 2k:size-8 4k:size-12" />
                                  )}
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "templates" && (
                <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <div className="bg-white rounded-2xl p-8 2k:p-14 4k:p-20 shadow-sm border border-black/5">
                    <div className="flex items-center gap-3 2k:gap-8 4k:gap-14 mb-10 2k:mb-18 4k:mb-24">
                      <div className="p-3 2k:p-6 4k:p-10 rounded-xl bg-black text-white">
                        <Layout className="w-6 h-6 2k:w-12 2k:h-12 4k:w-20 4k:h-20" />
                      </div>
                      <div>
                        <p className="text-sm 2k:text-2xl 4k:text-4xl text-muted-foreground font-medium mb-1">{t("account.nav.templates")}</p>
                        <h2 className="text-2xl 2k:text-5xl 4k:text-7xl font-bold">{t("account.templates.title")}</h2>
                      </div>
                    </div>

                    <WatermarkTemplateEditor />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Disconnect confirmation dialog */}
      <AlertDialog
        open={!!pendingDisconnect}
        onOpenChange={(open) => { if (!open) setPendingDisconnect(null); }}
      >
        <AlertDialogContent size="sm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingDisconnect?.platform === "instagram"
                ? t("instagram.disconnectConfirmTitle")
                : pendingDisconnect?.platform === "tiktok"
                  ? t("tiktok.disconnectConfirmTitle")
                  : t("youtube.disconnectConfirmTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDisconnect?.platform === "instagram"
                ? t("instagram.disconnectConfirmDesc")
                : pendingDisconnect?.platform === "tiktok"
                  ? t("tiktok.disconnectConfirmDesc")
                  : t("youtube.disconnectConfirmDesc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={handleConfirmDisconnect}
            >
              {pendingDisconnect?.platform === "instagram"
                ? t("instagram.disconnectConfirmAction")
                : pendingDisconnect?.platform === "tiktok"
                  ? t("tiktok.disconnectConfirmAction")
                  : t("youtube.disconnectConfirmAction")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
